import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeaders } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, Clapperboard, Camera, CheckCircle2, Loader2 } from 'lucide-react';
import { API_BASE } from '../config/api';

const API = API_BASE;
const PAGE_SIZE = 50;

const SceneList = () => {
  const { filmId } = useParams();
  const navigate = useNavigate();
  const [film, setFilm] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Fetch one page of scenes from the paginated endpoint.
  const fetchScenesPage = useCallback(async (pageNum) => {
    const res = await axios.get(
      `${API}/films/${filmId}/scenes?page=${pageNum}&page_size=${PAGE_SIZE}`,
      { headers: getAuthHeaders() }
    );
    return res.data; // { items, total, page, page_size, has_more }
  }, [filmId]);

  // Initial load: film meta + first page of scenes (in parallel).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [filmRes, pageRes] = await Promise.all([
          axios.get(`${API}/films/${filmId}`, { headers: getAuthHeaders() }),
          fetchScenesPage(1),
        ]);
        if (cancelled) return;
        setFilm(filmRes.data);
        setScenes(pageRes.items || []);
        setTotal(pageRes.total || 0);
        setHasMore(Boolean(pageRes.has_more));
        setPage(1);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [filmId, fetchScenesPage]);

  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const next = page + 1;
      const pageRes = await fetchScenesPage(next);
      setScenes((prev) => [...prev, ...(pageRes.items || [])]);
      setPage(next);
      setHasMore(Boolean(pageRes.has_more));
      // Trust server total in case scenes were added/removed between pages.
      if (typeof pageRes.total === 'number') setTotal(pageRes.total);
    } catch (error) {
      console.error('Failed to load more scenes:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!film) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-destructive">Film not found</div>
      </div>
    );
  }

  return (
    <div className="bg-background" data-testid="scene-list">
      <main className="container mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="flex items-center gap-2 md:gap-4 mb-6 md:mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/films/${filmId}`)}
            data-testid="back-button"
            className="flex-shrink-0 -ml-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            <span className="text-xs font-semibold tracking-wider uppercase">Back</span>
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl md:text-2xl font-bold font-display tracking-tight">
              All Scenes — {film.title}
            </h1>
            <p className="text-xs md:text-sm text-muted-foreground" data-testid="scene-count">
              {total} scene{total !== 1 ? 's' : ''}
              {scenes.length < total && (
                <span className="ml-2 opacity-70">(showing {scenes.length})</span>
              )}
            </p>
          </div>
        </div>
        {total === 0 ? (
          <div className="text-center py-12">
            <Clapperboard className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No scenes yet</h3>
            <p className="text-muted-foreground mb-6">
              Create scenes from the Scene Board
            </p>
            <Button onClick={() => navigate(`/films/${filmId}/board`)}>
              Open Scene Board
            </Button>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {scenes.map((scene) => {
                const plannedCount = scene.shot_count - scene.covered_shot_count;
                const progress = scene.shot_count > 0
                  ? Math.round((scene.covered_shot_count / scene.shot_count) * 100)
                  : 0;

                return (
                  <Card
                    key={scene.id}
                    className="p-6 hover:border-primary/50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/scenes/${scene.id}`)}
                    data-testid={`scene-item-${scene.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                      {/* Scene Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-sm font-mono font-semibold uppercase tracking-wider text-primary">
                            Scene {scene.scene_number}
                          </span>
                          <StatusBadge status={scene.status} />
                        </div>
                        {scene.description && (
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {scene.description}
                          </p>
                        )}
                      </div>

                      {/* Stats */}
                      <div className="flex gap-6 md:gap-8">
                        <div className="flex items-center gap-2">
                          <div className="h-10 w-10 rounded-sm bg-secondary flex items-center justify-center">
                            <Camera className="h-5 w-5 text-foreground" />
                          </div>
                          <div>
                            <div className="text-lg font-bold font-[Manrope]">
                              {scene.shot_count}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Total Shots
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <div className="h-10 w-10 rounded-sm bg-[#F59E0B]/20 flex items-center justify-center">
                            <Camera className="h-5 w-5 text-[#F59E0B]" />
                          </div>
                          <div>
                            <div className="text-lg font-bold font-[Manrope]">
                              {plannedCount}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Planned
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <div className="h-10 w-10 rounded-sm bg-primary flex items-center justify-center">
                            <CheckCircle2 className="h-5 w-5 text-primary-foreground" />
                          </div>
                          <div>
                            <div className="text-lg font-bold font-[Manrope]">
                              {scene.covered_shot_count}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Covered
                            </div>
                          </div>
                        </div>

                        {scene.shot_count > 0 && (
                          <div className="flex items-center gap-2">
                            <div className="h-10 w-10 rounded-sm bg-success/15 flex items-center justify-center">
                              <span className="text-xs font-bold text-success">
                                {progress}%
                              </span>
                            </div>
                            <div>
                              <div className="text-lg font-bold font-[Manrope]">
                                {progress}%
                              </div>
                              <div className="text-xs text-muted-foreground">
                                Complete
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            {hasMore && (
              <div className="flex justify-center mt-8">
                <Button
                  variant="outline"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  data-testid="load-more-scenes"
                >
                  {loadingMore ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>Load more ({total - scenes.length} remaining)</>
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const colors = {
    not_ready: 'bg-destructive/15 text-destructive',
    ready: 'bg-success/15 text-success',
    shot: 'bg-warning/15 text-warning',
    done: 'bg-info/15 text-info'
  };

  return (
    <span className={`px-2 py-1 rounded-sm text-xs font-medium ${colors[status]}`}>
      {status.replace('_', ' ')}
    </span>
  );
};

export default SceneList;
